import { defineStore } from 'pinia';
import Catalogue from '../../src/assets/catalogue.json';
import WeightsDefault from '../../src/assets/weights.json';
import Tags from '../../src/assets/tags.json';
import { CatalogInfo, Product, ShortMedia } from '../ifaces/bfeeds-external/Catalog.interface';
import { ref, watch } from 'vue';
import { flatten, unflatten } from 'flat'
import { type TypeProductCard } from '../components';
import Retailler from '../../src/assets/retaillers.json';
import Countries from '../../src/assets/countries.json';
import BynderData from '../../src/assets/bynder.json';
import { i18n, getLocale } from '../i18n';

export const VIDEO_PROVIDER = import.meta.env.VITE_VIDEO_PROVIDER || 'bynder'
export const useGift = defineStore("giftfinder", () => {
  const maison = '.veuveclicquot.com/'
  const defautRetailler = 'VC'
  const catalog = ref<CatalogInfo>()
  const tags = ref()
  const tagsKey = ref()
  const weights = ref()
  const freecaster = ref()
  const bynder = ref()
  const settings = ref()
  const businesspriority = ref()
  const gtm = ref()
  const byLang = ref<Record<string, CatalogInfo>>({} as Record<string, CatalogInfo>)
  const products = ref<Record<string, Product>>()
  const footer = ref<TypeProductCard[]>([])
  const fallbacks = ['en-US', 'fr-FR', 'es-ES']
  const getGift = (ean: string, evin:boolean = false, locale: string = 'en-US') => {
    let output =  products.value ? products.value[ean] : undefined
    if (evin) {
      const normLocale = locale.includes('-') ? `${locale.split('-')[0].toLowerCase()}-${locale.split('-')[1].toUpperCase()}` : locale
      const langEntry = byLang.value[normLocale] ?? byLang.value[locale]
      const imagesEvin = langEntry?.interfaceContent?.constraints?.evin?.products?.[ean]?.images
      if (imagesEvin?.lifestyle?.media && output) {
        const images = imagesEvin.lifestyle.media
        const arrayImages: ShortMedia[] = []
        const style = 'stylelife'
        Object.keys(images).map(el => {
          if(images[el]?.category && images[el].category === 'lifeStyle' && images[el].mime.includes('image')) {
            const format = {
              url: images[el].objects.original,
              type: style
            }
            arrayImages.push(format)
          }
        })

        const fiterImages = output?.images?.filter(el => el.type !== style)
        const newImages = fiterImages ? [ ...fiterImages, ...arrayImages] :  output.images
        output.images = newImages
      }
    }
    return output
  }
  const setCatalogInfo = async (value: any = Catalogue.data) => {
    setSettings(value.settings)
    const langKeys = value.langs.map((lang:any) => lang.iso)
    const flat: Record<string, string> = await flatten(value)

    let i18nMessage: { global: { previous: string; next: string; legal: string; notfound: string; loading: string; }; views: any; products: any } = { global: { previous: '', next: '', legal: '', notfound: '', loading: '' }, views: {}, products: {} }
    for (let lang of langKeys) {
      const output: Record<string, string> = {}

      for (let key in flat) {
        const whitelist = [...lang, 'eanupc', 'featuredVariant', 'tags', 'images', 'settings', 'eanupc', `.${lang}`]
        if (whitelist.some(item => key.includes(item))) {
          output[key] = flat[key]
        }
        const interfaceContent = ['interfaceContent']
        if (interfaceContent.some(item => key.includes(item))) {
          output[key.replace(`.${lang}`, '')] = flat[key]
        }
      }
      if (byLang.value) {
        if (byLang.value[lang] === undefined) {
          byLang.value[lang] = await unflatten(output)
        }
      }
    }
    const indexLang = langKeys.indexOf('en-US')
    langKeys.forEach((lang: string) => {
      const product = setProductsAndTags((byLang.value as Record<string, CatalogInfo>)[langKeys[indexLang]].products, lang)
      const catalogInfo = (byLang.value as Record<string, CatalogInfo>)[lang];
      i18nMessage = {
        global: catalogInfo.interfaceContent.global,
        views: catalogInfo.interfaceContent.views,
        products: product
      }
      if (i18n.global.availableLocales.includes(lang)) {
        i18n.global.mergeLocaleMessage(lang, i18nMessage)
      }
      const format = `${lang.split('-')[0].toLowerCase()}-${lang.split('-')[1].toLowerCase()}`
      i18n.global.setLocaleMessage(lang, i18nMessage)
      i18n.global.setLocaleMessage(format, i18nMessage)
      if (fallbacks.includes(lang)) {
        const key = lang.split('-')[0].toLowerCase()
        i18n.global.setLocaleMessage(key, i18nMessage)
      }
    })
    if (value?.settings?.evin) {
      let langEvin = value?.settings?.evin.split(',').map((el: string) => el.trim())
      if (value?.settings?.evin.includes('*')) {
        langEvin = i18n.global.availableLocales
      }
      if (langEvin.length > 0 && !langEvin.includes('none')) {
        langEvin.forEach((lang: string) => {
          const evinFlat: Record<string, string> = flatten(value?.interfaceContent?.constraints?.evin)
          let formatLang = lang.length === 2 ? lang : `${lang.split('-')[0].toLowerCase()}-${lang.split('-')[1].toUpperCase()}`
          let evinFilter = Object.keys(evinFlat).filter((key) => key.includes(formatLang))
          let i18nMessage: Record<string, string> = flatten(i18n.global.getLocaleMessage(formatLang))
          let isfallback = false
          let targetLang = formatLang
          if (value?.settings?.evin.includes('*') && lang.length === 2) {
            targetLang = fallbacks.find((iso) => iso.includes(lang.split('-')[0].toLowerCase())) as string
            evinFilter = Object.keys(evinFlat).filter((key) => key.includes(targetLang))
            formatLang = lang
          }
          if (Object.keys(i18nMessage).length === 0) {
            const fallback = lang.split('-')[0].toLowerCase()
            i18nMessage = flatten(i18n.global.getLocaleMessage(fallback))
            isfallback = Object.keys(i18nMessage).length > 0
            if (isfallback && fallbacks.some((iso) => !iso.includes(lang.split('-')[0].toLowerCase()))) {
              targetLang = fallbacks.find((iso) => iso.includes(lang.split('-')[0].toLowerCase())) as string
              evinFilter = Object.keys(evinFlat).filter((key) => key.includes(targetLang))
            }
          }
          Object.keys(i18nMessage).forEach((key) => {
            const target = `${key}.${targetLang}`
            if (evinFilter.includes(target)) {
              i18nMessage[key] = evinFlat[target]
            }
          })
          const unflattenMessage = unflatten(i18nMessage)
          i18n.global.setLocaleMessage(formatLang, unflattenMessage as unknown as any)
          i18n.global.setLocaleMessage(formatLang.toLowerCase(), unflattenMessage as unknown as any)
        })
      }
    }
    setFreecaster((byLang.value as Record<string, CatalogInfo>)[langKeys[indexLang]].interfaceContent.businesslogic.freecaster)
    // FORCE USE Tags.json bynder instead of API data (same pattern as businesspriority)
    console.log('Loading bynder from Tags.json:', (Tags as any).bynder)
    setBynderData((Tags as any).bynder)
    console.log('bynder.value after set:', bynder.value)
    setWeights()
    setTagskey()
    // FORCE USE Tags.json businesspriority instead of API data
    console.log('Loading businesspriority from Tags.json:', Tags.businesspriority)
    setBusinessPriority(Tags.businesspriority)
    console.log('businesspriority.value after set:', businesspriority.value)
    setGTM((byLang.value as Record<string, CatalogInfo>)[langKeys[indexLang]].interfaceContent?.businesslogic?.gtm)
  }

  const getWording = (locale: string) => {
    const catalogInfo = (byLang.value as Record<string, CatalogInfo>)[locale];
    return {
      global: catalogInfo.interfaceContent.global,
      views: catalogInfo.interfaceContent.views,
      products: catalogInfo.products as any
    }
  }
  const setProductsAndTags = (value: Product[], lang: string = 'en-US') => {
    const output: { [key: string]: any } = {}
    const tagsOutput: { [key: string]: string[] } = {}
    let images = []
    let videos = []
    const outputFooter: TypeProductCard[] = []
    for (let product of value) {
      const variants = product.variants.map((variant) => ({
        finish: variant.finish?.[lang] ?? variant.finish?.['en-US'],
        eanupc: variant.eanupc,
        shortDescription: variant.shortDescription?.[lang] ?? variant.shortDescription?.['en-US'],
        keysNotes: variant.keysNotes?.[lang] ?? variant.keysNotes?.['en-US'],
        tags: variant.tags,
        shades: variant.shades?.map(el => ({
          ...el,
          name: el.name?.[lang] ?? el.name?.['en-US']
        })),
        url: variant.url?.[lang] ?? variant.url?.['en-US']
      }))
      for (let variant of product.variants) {
        const packshots = []
        const stylelife = []
        const portrait = []
        const landscape = []
        const media = variant.media
        if (!media) continue
        for (let asset of media) {
          if (asset.category === 'packshot') {
            packshots.push({
              url: asset.objects.original,
              type: 'packshot'
            })
          }
          if (asset.category === 'lifeStyle') {
            stylelife.push({
              url: asset.objects.original,
              type: 'stylelife'
            })
          }
          if (asset.category === 'video') {
            landscape.push({
              url: asset.objects.original,
              type: 'landscape'
            })
          }
          if (asset.category === 'longVideo') {
            portrait.push({
              url: asset.objects.original,
              type: 'portrait'
            })
          }
        }
        images = [ ...(packshots || []), ...(stylelife || [])]
        videos = [ ...(landscape || []), ...(portrait || [])]
        if (variant.tags) {
          variant.tags.forEach((tag) => {
            if (!tagsOutput[tag]) {
              tagsOutput[tag] = []
            }
            tagsOutput[tag].push(variant.eanupc)
          })
        }

        if (!output[variant.eanupc]) output[variant.eanupc] = {}
        output[variant.eanupc] = {
          eanupc: variant.eanupc,
          name: product.name?.[lang] ?? product.name?.['en-US'],
          featuredVariant: product.featuredVariant,
          longDescription: variant.longDescription?.[lang] ?? variant.longDescription?.['en-US'],
          shortDescription: variant.shortDescription?.[lang] ?? variant.shortDescription?.['en-US'],
          benefit: variant.benefit?.[lang] ?? variant.benefit?.['en-US'],
          finish: variant.finish?.[lang] ?? variant.finish?.['en-US'],
          keysNotes: variant.keysNotes?.[lang] ?? variant.keysNotes?.['en-US'],
          variants: variants,
          images: images,
          videos: videos,
        }
        const hasFeatured = output[variant.eanupc]?.variants?.find((el:any) => {
          return el.eanupc === product.featuredVariant
        })
        if (hasFeatured === undefined) {
          const featured = variants?.[0]
          if (featured?.eanupc !== undefined) {
            output[variant.eanupc].featuredVariant = featured.eanupc
            output[variant.eanupc].variants = output[variant.eanupc].variants?.map((el: any) => {
              if (el.featuredVariant) el.featuredVariant = featured.eanupc
              return el
            })
          }
        }
        if (variant.eanupc === output[variant.eanupc].featuredVariant) {
          const packshot = variant.media?.find((el) => el.category === 'packshot')?.objects.original
          outputFooter.push({
            ean: variant.eanupc,
            name: product.name as unknown as string,
            image: typeof packshot === 'string' ? packshot : '',
          })
        }
      }
    }
    if (lang === getLocale(i18n)) {
      products.value = output
    }
    if (lang === 'en-US') {
      const order = settings.value?.sortFooter
      let footerSort = outputFooter.sort((a, b) => (a.name as unknown as Record<string, string>)['en-US'].localeCompare((b.name as unknown as Record<string, string>)['en-US']))
      if (order) {
        const itemsOrder = order.split(',').map((key: string) => key.trim()) || []
        if (itemsOrder.length > 0) {
          let output: TypeProductCard[] = []
          itemsOrder.forEach((key: string, index: number) => {
            const el = footerSort.find((el) => el.ean === key)
            if (el) {
              output[index] = el
            }
          })
          output = output.filter((el) => el !== undefined)
          output = Array.from(new Set([...output, ...footerSort]))
          footerSort = output
        }
      }
      footer.value = footerSort
      tags.value = tagsOutput
    }
    return output
  }

  const setFreecaster = (value: any) => {
    const output: Record<string, {
      [key: string]: string
    }> = {}

    Object.keys(value).forEach((key) => {
      output[key] = value[key]
    })
    freecaster.value = output
  }

  const getFreecaster = (ean: string, evin: boolean = false, locale: string = 'en-US') => {
    let output = freecaster.value?.[ean]
    if (evin && output) {
      const freecasterEvin = byLang.value[locale]?.interfaceContent?.constraints?.evin?.businesslogic?.freecaster?.[ean]
      if (freecasterEvin) {
        // Merge EVIN freecaster data with default freecaster data
        output = {
          ...output,
          ...freecasterEvin
        }
      }
    }
    return output
  }

  const setBynderData = (value: any) => {
    const output: Record<string, { [key: string]: string }> = {}
    // Use catalogue bynder data if available, otherwise fallback to static bynder.json
    const source = value || BynderData
    Object.keys(source).forEach((key) => {
      output[key] = source[key]
    })
    bynder.value = output
  }

  const getBynder = (ean: string, evin: boolean = false, locale: string = 'en-US') => {
    let output = bynder.value?.[ean]
    if (evin && output) {
      const bynderEvin = byLang.value[locale]?.interfaceContent?.constraints?.evin?.businesslogic?.bynder?.[ean]
      if (bynderEvin) {
        output = {
          ...output,
          ...bynderEvin
        }
      }
    }
    return output
  }

  const setWeights = () => {
    const output = WeightsDefault // TO FIX
    weights.value = output
  }
  const setTagskey = () => {
    const output = Tags
    tagsKey.value = output
  }
  const setSettings = (value: any) => {
    settings.value = value
  }
  const setBusinessPriority = (value: any) => {
    if (value && Object.keys(value)) {
      const output: Record<string, number> = {}
      Object.keys(value).forEach((key) => {
        output[key] = +value[key].value
      })

      businesspriority.value = output
    }
    else {
      // Fallback to Tags.json businesspriority
      const output: Record<string, number> = {}
      if (Tags.businesspriority) {
        Object.keys(Tags.businesspriority).forEach((key) => {
          const businessItem = (Tags.businesspriority as any)[key]
          if (businessItem && typeof businessItem.value !== 'undefined') {
            output[key] = +businessItem.value
          }
        })
      }
      businesspriority.value = output
    }
  }

  const setGTM = (value: any) => {
    if (value && Object.keys(value)) {
      const output: Record<string, string> = {}
      Object.keys(value).forEach((key) => {
        output[key] = value[key].value
      })
      gtm.value = output
    }
    else gtm.value = {}
  }

  const getGTMKey = (ean: string) => {
    const list = gtm.value
    if (list) {
      return list[ean] ? list[ean] : products.value?.[ean]?.name ?? ean
    }
    return undefined
  }
  const getCountries = (ean: string): {name: string, iso:string}[] => {
    const list: { [key: string]: any } = Retailler[ean as keyof typeof Retailler]
    if (!list) return []
    const isos = Object.keys(list).filter((key) => {
      return key !== 'en-INT'
    })
    let keys: Array<{iso: string, name: string}> = []
    for (let iso of isos) {
      const index = Countries.findIndex((country) =>  {
        return country.iso2 === iso || (country.locale.toLowerCase() === iso.toLowerCase() && country.enabled !== false)
      })
      if (index !== -1) {
        keys.push({
          name: Countries[index].name,
          iso: iso,
        })
      }
    }
    keys.sort((a, b) => a.name.localeCompare(b.name))
    return keys
  }
  const getRetailler = (ean: string, locale: string) => {
    const format = locale.includes('-') ? `${locale.split('-')[0].toLowerCase()}-${locale.split('-')[1].toUpperCase()}` : locale.toUpperCase()
    const list = Retailler[ean as keyof typeof Retailler]
    let output = []
    const defaultList = [{
      name: defautRetailler,
      url: products.value?.[ean]?.variants?.[0]?.url ?? maison
    }]
    if (list) {
      if (locale === 'en-int') {
        Object.keys(list).forEach((key) => {
          if (key.length === 2) {
            output = [...list[key as keyof typeof list]]
          }
        })
      } else(
        output = Array.isArray(list[format as keyof typeof list]) ? list[format as keyof typeof list] as unknown as any[] : []
      )
      if (output.length === 0) output = defaultList
    } else {
      output = defaultList
    }
    return output
  }

  const isMaison = (url: string) => {
    return url.includes(maison)
  }

  watch(() => (i18n.global.locale as any).value, (newLocale: string) => {
    const src = byLang.value?.['en-US']?.products as any
    if (src) setProductsAndTags(src, newLocale)
  })
  return { setCatalogInfo, catalog, setProductsAndTags, products, tags, tagsKey, setFreecaster, freecaster, getFreecaster, setBynderData, bynder, getBynder, setWeights, weights, setTagskey, getGift, setSettings, settings, footer, getRetailler, getWording, defautRetailler, getCountries, isMaison, businesspriority, getGTMKey, gtm }

});

